import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useStore } from '@/store/useStore';
import type { Product } from '@/types/product';

export default function ProductPage() {
  const { productId } = useParams<{ productId: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [selectedColour, setSelectedColour] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);

  const addToCart = useStore(state => state.addToCart);
  const addToWishlist = useStore(state => state.addToWishlist);
  const isInWishlist = useStore(state => state.isInWishlist(productId || ''));

  useEffect(() => {
    loadProduct();
  }, [productId]);

  const loadProduct = async () => {
    if (!productId) return;

    setLoading(true);
    try {
      const docRef = doc(db, 'products', productId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const prod = { id: docSnap.id, ...docSnap.data() } as Product;
        setProduct(prod);
        setSelectedColour(prod.colours[0] || '');
        setSelectedSize(prod.sizes?.[0] || '');
      }
    } catch (error) {
      console.error('Error loading product:', error);
    }
    setLoading(false);
  };

  const handleAddToCart = () => {
    if (!product) return;
    addToCart(product, selectedColour, selectedSize, quantity);
  };

  const stockKey = selectedSize ? `${selectedColour}-${selectedSize}` : selectedColour;
  const availableStock = product?.stock[stockKey] || 0;
  const isInStock = availableStock > 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-warm flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-forest border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-warm flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-serif font-bold text-slate mb-2">Product Not Found</h2>
          <p className="text-gray-600">This product doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Image */}
          <div className="sticky top-24 self-start">
            <div className="aspect-square rounded-lg overflow-hidden bg-white">
              <img
                src={product.imageUrl}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Details */}
          <div>
            <div className="bg-white rounded-lg p-8">
              <p className="text-sm text-gray-500 uppercase tracking-wide mb-2">{product.brand}</p>
              <h1 className="text-3xl font-serif font-bold text-slate mb-4">{product.name}</h1>

              {/* Rating */}
              <div className="flex items-center gap-2 mb-4">
                <div className="flex text-amber">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className={`w-5 h-5 ${i < Math.floor(product.rating) ? 'fill-current' : 'fill-gray-300'}`}
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <span className="text-sm text-gray-600">{product.rating} ({product.reviewCount} reviews)</span>
              </div>

              {/* Price */}
              <div className="mb-6">
                {product.salePrice ? (
                  <div className="flex items-baseline gap-3">
                    <span className="text-3xl font-bold text-red-600">£{product.salePrice.toFixed(2)}</span>
                    <span className="text-xl text-gray-500 line-through">£{product.price.toFixed(2)}</span>
                    <span className="text-sm font-semibold text-red-600 bg-red-50 px-2 py-1 rounded">
                      Save £{(product.price - product.salePrice).toFixed(2)}
                    </span>
                  </div>
                ) : (
                  <span className="text-3xl font-bold text-slate">£{product.price.toFixed(2)}</span>
                )}
              </div>

              <p className="text-gray-700 mb-6">{product.description}</p>

              {/* Colour Selector */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-slate mb-2">
                  Colour: {selectedColour}
                </label>
                <div className="flex flex-wrap gap-2">
                  {product.colours.map(colour => (
                    <button
                      key={colour}
                      onClick={() => setSelectedColour(colour)}
                      className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                        selectedColour === colour
                          ? 'border-forest bg-forest text-warm'
                          : 'border-gray-300 hover:border-forest'
                      }`}
                    >
                      {colour}
                    </button>
                  ))}
                </div>
              </div>

              {/* Size Selector */}
              {product.sizes && product.sizes.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-slate mb-2">
                    Size: {selectedSize}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {product.sizes.map(size => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                          selectedSize === size
                            ? 'border-forest bg-forest text-warm'
                            : 'border-gray-300 hover:border-forest'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Stock Status */}
              <div className="mb-6">
                {isInStock ? (
                  <p className="text-green-600 font-medium">
                    ✓ In stock ({availableStock} available)
                  </p>
                ) : (
                  <p className="text-red-600 font-medium">✗ Out of stock</p>
                )}
              </div>

              {/* Quantity */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-slate mb-2">Quantity</label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center"
                  />
                  <button
                    onClick={() => setQuantity(Math.min(availableStock, quantity + 1))}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4 mb-8">
                <button
                  onClick={handleAddToCart}
                  disabled={!isInStock}
                  className="flex-1 px-8 py-4 bg-forest text-warm font-semibold rounded-lg hover:bg-forest-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add to Basket
                </button>
                <button
                  onClick={() => isInWishlist ? null : addToWishlist(product.id)}
                  className={`px-6 py-4 rounded-lg border-2 transition-colors ${
                    isInWishlist
                      ? 'border-red-500 bg-red-50 text-red-500'
                      : 'border-gray-300 hover:border-forest'
                  }`}
                >
                  <svg className="w-6 h-6" fill={isInWishlist ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </button>
              </div>

              {/* Features */}
              <div className="border-t pt-6">
                <h3 className="font-semibold text-slate mb-3">Features</h3>
                <ul className="space-y-2">
                  {product.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-700">
                      <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
